import NumberService from './NumberService';
import { DiscountType } from '@/views/modules/shared/constants/ProductTypes';

interface Product {
   id: number;
   product_unit_warehouse_id?: number;
   transport_product_id?: number;
   unit_price: string;
   total: string;
   net_total: string;
   quantity: string;
   serial_numbers?: string[];
   discount_type: DiscountType;
   discount_value: number;
}

interface ProductStock {
   id: number;
   warehouse_invoice_id: number;
   product_id: number;
   quantity: string;
   smallest_unit_quantity: string;
   expiry_date: string;
   serial_numbers: string[];
   lot_number: string;
}

interface InvoiceBinded {
   id: number;
   products: Product[];
}

function getProductId(product: Product) {
   return product.product_unit_warehouse_id || product.transport_product_id;
}

export class BindingInvoiceService {
   static getProducts(
      products: Product[],
      remainingProducts: Record<number, { quantity: number; total_discount: number }>,
      productsStock: ProductStock[] = [],
      options: {
         hasDiscountNotice: boolean;
         invoice_binded: InvoiceBinded | null;
         can_exceed_limit: boolean;
      } = {
         hasDiscountNotice: false,
         invoice_binded: null,
         can_exceed_limit: false,
      },
   ) {
      const {
         hasDiscountNotice = false,
         invoice_binded = null,
         can_exceed_limit = false,
      } = options;

      const serialNumbers = productsStock.reduce((res, stock) => {
         if (stock.serial_numbers) res.push(...stock.serial_numbers);

         return res;
      }, []);

      return products
         .map((p) => {
            const bindedProduct = invoice_binded?.products.find(
               (product) => getProductId(product) === getProductId(p),
            );
            const remainingProduct = remainingProducts[getProductId(p)];

            const discount_type = p.discount_type;
            let discount_value = p.discount_value;

            if (discount_type == DiscountType.FIXED) {
               if (remainingProduct?.total_discount != null) {
                  discount_value = remainingProduct.total_discount;
               }
            }

            const unit_price = hasDiscountNotice
               ? NumberService.parseFloat(p.unit_price) -
                 NumberService.parseFloat(bindedProduct?.unit_price || '0')
               : p.unit_price;

            const total = hasDiscountNotice
               ? NumberService.parseFloat(p.total) -
                 NumberService.parseFloat(bindedProduct?.total || '0')
               : p.total;

            const net_total = hasDiscountNotice
               ? NumberService.parseFloat(p.net_total) -
                 NumberService.parseFloat(bindedProduct?.net_total || '0')
               : p.net_total;

            return {
               ...p,
               unit_price,
               discount_value,
               total,
               net_total,
               quantity: Math.min(
                  parseFloat(p.quantity.replace(/,/g, '')),
                  !hasDiscountNotice && !can_exceed_limit
                     ? remainingProduct?.quantity
                     : Number.POSITIVE_INFINITY,
               ),
               max_quantity:
                  !hasDiscountNotice && !can_exceed_limit
                     ? remainingProduct?.quantity
                     : Number.POSITIVE_INFINITY,
               serial_numbers: p.serial_numbers
                  ? p.serial_numbers.filter((serialNumber) => serialNumbers.includes(serialNumber))
                  : undefined,
            };
         })
         .filter((p) => can_exceed_limit || p.quantity > 0);
   }
}
